// The Square's world: the layout (from config/community/square.yml plus a
// seed), the ground layer, and the upright sprites the map depth-sorts
// against walkers every frame.
//
//   buildWorld(config) → {
//     W, H, byKey, landmarks, blocked(x, y), findPath(from, to),
//     paintGround(ctx, pal),          // everything flat, painted once
//     sprites: [{ x, y, box, paint, kind, sway }],  // upright, sorted by y
//     lights:  [{ x, y, r }],          // warm glows after dark
//   }
//
// Known elements (landmarks, junctions, roads, the river, the lake, the pond,
// spawn) stay put; trees, groves, shrubs, rocks,
// flowers and the bends in the roads grow from the seed, so a new seed grows a
// new Square around the same places.
//
// The map is large (8000×5000), so everything that asks "what is near here?"
// goes through a spatial hash rather than a scan, and walking a long way is
// done by pathfinding over a coarse walkability grid (findPath).
//
// The original map's scenery is still grown at its own width (config
// mainland_width) from its own random stream, and the Northern Reach east of
// it (north.js, reach_places.js) from streams of its own, so adding the Reach
// never moved a tree of the original map.
import { rng, noise2, softShadow, wash, blobPath, samplePath, inkEllipse, line, withAlpha } from "./paint"
import { paintOak, paintPine, paintBush, paintRock, paintReeds, paintMushrooms, paintLog, paintFlowerClump, paintTuft } from "./nature"
import { buildLandmarks, lampPost, welcomeArch } from "./landmarks"
import { buildDistricts } from "./districts"
import { buildSouthlands } from "./southlands"
import { buildReach } from "./north"
import { buildReachPlaces } from "./reach_places"
import { buildPeaks } from "./peaks"
import { buildIsles } from "./isles"
import { buildDunes } from "./dunes"
import { buildWildwood } from "./wildwood"
import { buildTransport } from "./transport"

export { PALETTES } from "./palette"
export { rng }
export const HAND_FONT = '"Bradley Hand", "Segoe Print", "Chalkboard SE", "Comic Neue", "Comic Sans MS", cursive'

// Points (or circles) bucketed by cell, for fast "anything near (x, y)?".
class SpatialHash {
    constructor(cell = 64) { this.cell = cell; this.map = new Map() }
    key(cx, cy) { return cx * 100003 + cy }
    insert(item, x, y, r = 0) {
        const c = this.cell
        for (let cx = Math.floor((x - r) / c); cx <= Math.floor((x + r) / c); cx++) {
            for (let cy = Math.floor((y - r) / c); cy <= Math.floor((y + r) / c); cy++) {
                const k = this.key(cx, cy)
                let bucket = this.map.get(k)
                if (!bucket) { bucket = []; this.map.set(k, bucket) }
                bucket.push(item)
            }
        }
    }
    near(x, y, r) {
        const c = this.cell, out = new Set()
        for (let cx = Math.floor((x - r) / c); cx <= Math.floor((x + r) / c); cx++) {
            for (let cy = Math.floor((y - r) / c); cy <= Math.floor((y + r) / c); cy++) {
                const bucket = this.map.get(this.key(cx, cy))
                if (bucket) bucket.forEach((item) => out.add(item))
            }
        }
        return out
    }
    // Allocation-free: this runs on every step a walker takes.
    any(x, y, r, test) {
        const c = this.cell
        for (let cx = Math.floor((x - r) / c); cx <= Math.floor((x + r) / c); cx++) {
            for (let cy = Math.floor((y - r) / c); cy <= Math.floor((y + r) / c); cy++) {
                const bucket = this.map.get(this.key(cx, cy))
                if (bucket) for (let i = 0; i < bucket.length; i++) if (test(bucket[i])) return true
            }
        }
        return false
    }
}

export function buildWorld(config) {
    const W = config.width, H = config.height
    const Wm = Math.min(W, config.mainland_width || W) // the original map's width
    const Hm = Math.min(H, config.mainland_height || H) // ...and its height
    // Every later section grows inside its own bounds, from its own streams.
    const section = (key) => (config.sections || []).find((sec) => sec.key === key)
    const reachSec = section("reach") || { x: Wm, y: 0, w: W - Wm, h: H }
    const reachRight = reachSec.x + reachSec.w, reachBottom = reachSec.y + reachSec.h
    const reach = buildReach(config, { W: reachRight, H: reachBottom, Wm, edges: { right: reachRight >= W, bottom: reachBottom >= H } })
    const peaksSec = section("peaks")
    const peaks = buildPeaks(config, peaksSec ? { x0: peaksSec.x, x1: peaksSec.x + peaksSec.w, y1: peaksSec.y + peaksSec.h, edges: { bottom: peaksSec.y + peaksSec.h >= H } } : { x0: W, x1: W, y1: 0 })
    const peaksX = peaksSec ? peaksSec.x : Infinity
    const bounds = (sec) => (sec ? { x0: sec.x, y0: sec.y, x1: sec.x + sec.w, y1: sec.y + sec.h } : { x0: 0, y0: 0, x1: 0, y1: 0 })
    const islesSec = section("isles"), dunesSec = section("dunes"), wildSec = section("wildwood")
    const isles = buildIsles(config, bounds(islesSec))
    const dunes = buildDunes(config, bounds(dunesSec))
    const wildwood = buildWildwood(config, bounds(wildSec))
    const southY = islesSec || dunesSec || wildSec ? Math.min(...[islesSec, dunesSec, wildSec].filter(Boolean).map((sec) => sec.y)) : Infinity
    // which section's rules apply at (x, y)
    const groundPal = (pal, x, y) => (y < southY && x >= peaksX ? peaks.groundPal(pal, x, y) : y < southY ? reach.groundPal(pal, x, y) : pal)
    const biome = (x, y) => {
        if (y >= southY) {
            const forest = wildwood.biome(x, y)
            if (forest) return forest
            if (dunesSec && x >= dunesSec.x) return dunes.biome(x, y)
            if (isles.islandAt(x, y, -20)) return "tropical"
            if (y < southY + 400 && x < (dunesSec ? dunesSec.x : Infinity)) return reach.biome(x, y) // the Square's south shore
            return "sea"
        }
        return x >= peaksX ? peaks.biome(x, y) : reach.biome(x, y)
    }
    // land in the sea: the Reach's islands and the Warm Isles
    const islandAt = (x, y, pad = 0) => reach.islandAt(x, y, pad) || isles.islandAt(x, y, pad)
    // water on land that blocks walking: the atoll's lagoon, the oasis, the falls' pool
    const inLandWater = (x, y, pad = 0) => isles.inWater(x, y, pad) || dunes.inWater(x, y, pad) || wildwood.inWater(x, y, pad)
    const landmarks = config.landmarks
    const byKey = Object.fromEntries(landmarks.map((l) => [l.key, l]))
    const pond = config.pond
    const lake = config.lake
    const spawn = config.spawn
    const archAt = config.arch || { x: spawn.x, y: H - 26 }
    const r = rng(config.seed)
    const n = noise2(config.seed)

    // --- roads: the ring through the junctions, then every road in the layout,
    // each bent once by the seed (longer roads bend more)
    const junctions = Object.fromEntries(Object.entries(config.junctions || {}).map(([k, [x, y]]) => [k, { x, y }]))
    const point = (ref) => {
        if (ref === "spawn") return spawn
        if (ref === "arch") return archAt
        if (junctions[ref]) return junctions[ref]
        const l = byKey[ref]
        return l ? { x: l.x, y: l.y + 20 } : null
    }
    const bend = (a, b, scale = 0.14) => {
        const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2
        const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1
        const off = (r() - 0.5) * Math.min(170, len * scale)
        return [[a.x, a.y], [mx - (dy / len) * off, my + (dx / len) * off], [b.x, b.y]]
    }
    const paths = []
    const ring = config.ring || []
    ring.forEach((k, i) => paths.push(bend(junctions[k], junctions[ring[(i + 1) % ring.length]], 0.06)))
    ;(config.roads || []).forEach(([a, b]) => {
        const pa = point(a), pb = point(b)
        if (pa && pb) paths.push(bend(pa, pb))
    })
    const pathSamples = paths.map((p) => samplePath(p, 5))
    const roadHash = new SpatialHash(48)
    pathSamples.forEach((samples) => samples.forEach((p, i) => { if (i % 2 === 0) roadHash.insert(p, p.x, p.y) }))
    // the Reach's roads, bent by their own stream
    const rr = rng(config.seed + 401)
    const reachPaths = []
    ;(config.reach_roads || []).forEach(([a, b]) => {
        const pa = point(a), pb = point(b)
        if (!pa || !pb) return
        const mx = (pa.x + pb.x) / 2, my = (pa.y + pb.y) / 2, dx = pb.x - pa.x, dy = pb.y - pa.y, len = Math.hypot(dx, dy) || 1
        const off = (rr() - 0.5) * Math.min(170, len * 0.14)
        reachPaths.push([[pa.x, pa.y], [mx - (dy / len) * off, my + (dx / len) * off], [pb.x, pb.y]])
    })
    const reachSamples = reachPaths.map((p) => samplePath(p, 5))
    const reachRoadHash = new SpatialHash(48)
    reachSamples.forEach((samples) => samples.forEach((p, i) => { if (i % 2 === 0) reachRoadHash.insert(p, p.x, p.y) }))
    const nearReachPath = (x, y, pad) => reachRoadHash.any(x, y, pad, (p) => Math.hypot(p.x - x, p.y - y) < pad)
    // trails: narrow paths through the wild, with no lamps (their own stream)
    const rt = rng(config.seed + 402)
    const trailSamples = []
    const trailHash = new SpatialHash(48), southTrailHash = new SpatialHash(48)
    ;[...(config.trails || []).map((t) => [t, trailHash]), ...(config.south_trails || []).map((t) => [t, southTrailHash])].forEach(([[a, b], hash]) => {
        const pa = point(a), pb = point(b)
        if (!pa || !pb) return
        const mx = (pa.x + pb.x) / 2, my = (pa.y + pb.y) / 2, dx = pb.x - pa.x, dy = pb.y - pa.y, len = Math.hypot(dx, dy) || 1
        const off = (rt() - 0.5) * Math.min(260, len * 0.2)
        const samples = samplePath([[pa.x, pa.y], [mx - (dy / len) * off, my + (dx / len) * off], [pb.x, pb.y]], 5)
        trailSamples.push(samples)
        samples.forEach((p, i) => { if (i % 2 === 0) hash.insert(p, p.x, p.y) })
    })
    const nearTrail = (x, y, pad) => trailHash.any(x, y, pad, (p) => Math.hypot(p.x - x, p.y - y) < pad)
    const nearSouthTrail = (x, y, pad) => southTrailHash.any(x, y, pad, (p) => Math.hypot(p.x - x, p.y - y) < pad)
    // What a spot must keep clear of depends on who is asking, so adding to
    // the world never reshuffles what was already grown. Each land grows in
    // its own layer, seeing only what existed when it was made: false is the
    // original map; "reach" adds the Reach's roads; "peaks" adds the first
    // trails; true (the south, and painting) is everything.
    const later = (level, since) => level === true || (since === "peaks" && level === "peaks")
    const nearPath = (x, y, pad, withReach = true) => roadHash.any(x, y, pad, (p) => Math.hypot(p.x - x, p.y - y) < pad) || (withReach && nearReachPath(x, y, pad)) || (later(withReach, "peaks") && nearTrail(x, y, pad)) || (withReach === true && nearSouthTrail(x, y, pad))

    // --- the rivers (the river and the brook) and their bridges, wherever a
    // road crosses one
    const rivers = [config.river, config.brook].filter(Boolean).map((rv) => ({ width: rv.width, sound: rv.sound, samples: samplePath(rv.points, 6) }))
    const mainlandRivers = rivers.slice() // the original map's reeds grow along these only
    ;[config.peaks_river, config.south_river].filter(Boolean).forEach((rv) => rivers.push({ width: rv.width, sound: rv.sound, samples: samplePath(rv.points, 6) }))
    const river = rivers[0] || null
    const riverHash = new SpatialHash(48)
    rivers.forEach((rv) => rv.samples.forEach((p) => riverHash.insert({ x: p.x, y: p.y, half: rv.width / 2 }, p.x, p.y)))
    const inRiver = (x, y, pad) => riverHash.any(x, y, 30 + pad, (p) => Math.hypot(p.x - x, p.y - y) < p.half + pad)

    // --- the sea: everything south-east of the coastline, carried on east
    // and north up the fjord by the Reach's coast
    const coast = config.coast ? samplePath(config.coast, 8) : null
    const northCoast = reach.northCoast
    // The sea's shore, west to east along the north side, then (with a south
    // row) back west along the Dunes and round the map's south-west corner to
    // the Square's south-west shore.
    const ringed = !!(config.south_coast && config.dunes_west_coast && config.west_coast)
    const coastLine = config.coast ? [
        ...(ringed ? config.west_coast.slice(0, -1) : []),
        ...config.coast, ...(config.north_coast || []).slice(1), ...(config.peaks_coast || []).slice(1),
        ...(ringed ? [...config.south_coast.slice(1), ...config.dunes_west_coast.slice(1)] : [])
    ] : null
    const seaPolygon = coastLine ? (ringed ? [...coastLine, [-60, H + 60]] : [...coastLine, [W + 60, H + 60], [config.coast[0][0], H + 60]]) : null
    const coastHash = new SpatialHash(64)
    if (coast) coast.forEach((p) => coastHash.insert(p, p.x, p.y))
    northCoast.forEach((p) => coastHash.insert(p, p.x, p.y))
    const peaksCoast = config.peaks_coast ? samplePath(config.peaks_coast, 8) : []
    peaksCoast.forEach((p) => coastHash.insert(p, p.x, p.y))
    const southShores = [config.south_coast, config.dunes_west_coast, config.west_coast].filter(Boolean).map((pts) => samplePath(pts, 8))
    const southCoastHash = new SpatialHash(64)
    southShores.forEach((line) => line.forEach((p) => southCoastHash.insert(p, p.x, p.y)))
    const inSeaPolygon = (x, y) => {
        if (!seaPolygon) return false
        let inside = false
        for (let i = 0, j = seaPolygon.length - 1; i < seaPolygon.length; j = i++) {
            const [xi, yi] = seaPolygon[i], [xj, yj] = seaPolygon[j]
            if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
        }
        return inside
    }
    // `all` false: only the shores and islands the north row was grown with
    const nearCoast = (x, y, pad, all = true) => !!coast && (coastHash.any(x, y, pad, (p) => Math.hypot(p.x - x, p.y - y) < pad) || (all && southCoastHash.any(x, y, pad, (p) => Math.hypot(p.x - x, p.y - y) < pad)))
    // an island is land in the sea; its beach is kept open like the coast's
    const inSea = (x, y, pad = 0, all = true) => (inSeaPolygon(x, y) ? !(all ? islandAt : reach.islandAt)(x, y, Math.max(0, Math.min(pad, 60))) : pad > 0 && nearCoast(x, y, pad, all))

    const bridges = []
    if (rivers.length) {
        ;[...pathSamples, ...trailSamples].forEach((samples) => {
            let run = []
            samples.forEach((p) => {
                if (inRiver(p.x, p.y, 10)) run.push(p)
                else if (run.length) { bridges.push(bridgeFrom(run)); run = [] }
            })
            if (run.length) bridges.push(bridgeFrom(run))
        })
    }
    function bridgeFrom(run) {
        const a = run[0], b = run[run.length - 1]
        const angle = Math.atan2(b.y - a.y, b.x - a.x)
        const pad = 16
        return { x1: a.x - Math.cos(angle) * pad, y1: a.y - Math.sin(angle) * pad, x2: b.x + Math.cos(angle) * pad, y2: b.y + Math.sin(angle) * pad, angle }
    }
    const segDist = (px, py, ax, ay, bx, by) => {
        const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy
        let t = l2 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0
        t = Math.max(0, Math.min(1, t))
        return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
    }
    const onBridge = (x, y) => bridges.some((b) => segDist(x, y, b.x1, b.y1, b.x2, b.y2) < 20)

    const inEllipse = (e, x, y, pad) => !!e && ((x - e.x) / (e.rx + pad)) ** 2 + ((y - e.y) / (e.ry + pad)) ** 2 < 1
    const inPond = (x, y, pad) => inEllipse(pond, x, y, pad)
    const inLake = (x, y, pad) => inEllipse(lake, x, y, pad)

    // --- getting around: the Grand Loop, the cable car, the balloons. They came
    // after everything else, so nothing grown before them sees their stations
    // (`placed` below); whatever grew on a line is cleared after the fact
    // (`offLine`), without a single random draw changing.
    const transport = buildTransport(config, { byKey, inSeaPolygon, islandAt, inRiver, onGlacier: (x, y) => reach.onGlacier(x, y), nearPath, biome })
    const placed = landmarks.filter((l) => !l.line)
    const offLine = (o, k = 0.6) => !transport.near(o.x, o.y, 20 + (o.s || 10) * k)

    // --- built places: the village's, the districts', the southlands', the arch
    const built = buildLandmarks(byKey)
    const districts = buildDistricts(byKey, { lake, river })
    const south = buildSouthlands(byKey, { coast: config.coast, seed: config.seed })
    const reachPlaces = buildReachPlaces(byKey, { seed: config.seed, reach })
    ;[south, reachPlaces].forEach((part) => {
        ;["sprites", "decals", "fields", "footprints", "lights", "walkways"].forEach((k) => districts[k].push(...(part[k] || [])))
        Object.assign(districts.anchors, part.anchors || {})
    })
    const arch = welcomeArch(archAt.x, archAt.y)
    const footprints = [...built.footprints, ...districts.footprints, ...arch.footprints]
    const sprites = [...built.sprites, ...districts.sprites].map((s) => ({ ...s, kind: s.kind || "built" }))
    sprites.push({ ...arch, kind: "built" })
    const lights = [...built.lights, ...districts.lights]
    const walkways = districts.walkways || [] // piers and jetties over water

    // each landmark's name tag hangs just below it; keep that strip clear
    const labelBoxes = placed.map((l) => ({ x: l.x - 90, y: l.y + l.r * 0.5, w: 180, h: 56 }))
    // and nothing grows through a building: every built sprite's drawn outline
    const builtBoxes = new SpatialHash(96)
    sprites.forEach((sp) => {
        const b = { x1: sp.x + sp.box[0], x2: sp.x + sp.box[2], y1: sp.y + sp.box[1], y2: sp.y + sp.box[3] + 14 }
        builtBoxes.insert(b, (b.x1 + b.x2) / 2, (b.y1 + b.y2) / 2, Math.hypot(b.x2 - b.x1, b.y2 - b.y1) / 2)
    })
    const insideBuilt = (x, y, pad) => builtBoxes.any(x, y, pad + 4, (b) => x > b.x1 - pad && x < b.x2 + pad && y > b.y1 - pad && y < b.y2 + pad)
    const clearOf = (x, y, pad, withReach = true) => {
        if (insideBuilt(x, y, Math.max(0, pad))) return false
        if (placed.some((l) => Math.hypot(l.x - x, (l.y - y) * 1.15) < l.r + pad)) return false
        if (labelBoxes.some((b) => x > b.x - pad && x < b.x + b.w + pad && y > b.y - pad && y < b.y + b.h + pad + 20)) return false
        if (inPond(x, y, pad + 6) || inLake(x, y, pad + 14)) return false
        if (inRiver(x, y, pad + 10)) return false
        if (withReach && peaks.inLake(x, y, pad + 12)) return false
        if (withReach === true && inLandWater(x, y, pad + 12)) return false
        if (inSea(x, y, 110 + pad, withReach === true)) return false // the beach stays open
        if (nearPath(x, y, 30 + pad, withReach)) return false
        if (withReach && (reach.onIce(x, y, -pad) || reach.onGlacier(x, y) || reach.onFerry(x, y, 40 + pad))) return false
        if (Math.hypot(spawn.x - x, spawn.y - y) < 90 + pad) return false
        return true
    }

    // lamp posts along the roads, alternating sides
    const lampHash = new SpatialHash(128)
    paths.forEach((p, pi) => {
        const samples = pathSamples[pi]
        for (let i = 30; i < samples.length - 24; i += 48) {
            const s = samples[i], side = (i / 48 + pi) % 2 ? 1 : -1
            const x = s.x + s.nx * 34 * side, y = s.y + s.ny * 34 * side
            if (!clearOf(x, y, -18)) continue
            if (lampHash.any(x, y, 210, (o) => Math.hypot(o.x - x, o.y - y) < 210)) continue
            if (transport.near(x, y, 14)) continue
            const lamp = lampPost(x, y)
            sprites.push({ ...lamp, kind: "lamp" })
            lampHash.insert({ x, y }, x, y)
            lights.push(lamp.light)
            footprints.push({ circle: true, x, y: y - 2, r: 5 })
        }
    })

    // --- trees: a forest edge, groves where the noise gathers them, and a
    // light scattering over the open meadow
    const trees = []
    const treeHash = new SpatialHash(64)
    const grove = noise2(config.seed + 9)
    const target = Math.round(170 * (Wm * Hm) / (2000 * 1250) * 0.9)
    for (let i = 0; i < target * 14 && trees.length < target; i++) {
        let x, y
        const mode = i % 5
        if (mode < 3) {
            const side = Math.floor(r() * 4), along = r(), depth = r() * r() * 170
            x = side === 0 ? depth : side === 1 ? Wm - depth : along * Wm
            y = side === 2 ? depth : side === 3 ? Hm - depth * 0.6 : along * Hm
        } else {
            x = 50 + r() * (Wm - 100)
            y = 50 + r() * (Hm - 100)
            // inside the map, trees gather in groves
            if (grove(x / 420, y / 420) < (mode === 3 ? 0.58 : 0.7)) continue
        }
        const nearEdge = Math.min(x, Wm - x, y, Hm - y) < 140
        const kind = nearEdge ? (r() < 0.55 ? "pine" : "oak") : (r() < 0.25 ? "pine" : "oak")
        const size = kind === "pine" ? 18 + r() * 12 : 20 + r() * 14
        if (!clearOf(x, y, size * 0.6, false)) continue
        if (treeHash.any(x, y, size * 2.2, (o) => Math.hypot(o.x - x, o.y - y) < (o.s + size) * 0.95)) continue
        const t = { x, y, s: size, kind, seed: Math.floor(r() * 1e6) }
        trees.push(t)
        treeHash.insert(t, x, y)
    }
    // the Reach's roads come later than the original map's scenery: clear
    // the few trees they run through, without redrawing the rest
    const onReachRoad = (o) => nearReachPath(o.x, o.y, 30 + o.s * 0.6)
    for (let i = trees.length - 1; i >= 0; i--) if (onReachRoad(trees[i]) || !offLine(trees[i])) trees.splice(i, 1)
    trees.forEach((t) => {
        const paint = t.kind === "pine" ? paintPine : paintOak
        const h = t.kind === "pine" ? t.s * 3.6 : t.s * 3
        sprites.push({ x: t.x, y: t.y, box: [-t.s * 1.35, -h, t.s * 1.35, 6], kind: "tree", sway: 1, seed: t.seed, paint(ctx, pal) { paint(ctx, pal, t.s, t.seed) } })
        footprints.push({ circle: true, x: t.x, y: t.y - 2, r: t.s * 0.28 + 4 })
    })

    // --- shrubs, rocks, mushrooms, logs, reeds
    const smalls = []
    const area = (Wm * Hm) / (2000 * 1250)
    const place = (count, kind, sizeFn, pad) => {
        for (let i = 0, made = 0; i < count * 30 && made < count; i++) {
            const x = 40 + r() * (Wm - 80), y = 40 + r() * (Hm - 80), s = sizeFn()
            if (!clearOf(x, y, pad, false)) continue
            if (treeHash.any(x, y, s + 60, (o) => Math.hypot(o.x - x, o.y - y) < (o.s || 20) + s + 8)) continue
            const o = { x, y, s, kind, seed: Math.floor(r() * 1e6) }
            smalls.push(o)
            treeHash.insert(o, x, y)
            made++
        }
    }
    place(Math.round(46 * area), "bush", () => 11 + r() * 7, 8)
    place(Math.round(18 * area), "rock", () => 8 + r() * 9, 6)
    place(Math.round(9 * area), "mushrooms", () => 9 + r() * 4, 4)
    place(Math.round(4 * area), "log", () => 20 + r() * 6, 10)
    const painters = { bush: paintBush, rock: paintRock, mushrooms: paintMushrooms, log: (ctx, pal, s, seed) => paintLog(ctx, pal, s, (seed % 7) / 7 - 0.5) }
    for (let i = smalls.length - 1; i >= 0; i--) if (onReachRoad(smalls[i]) || !offLine(smalls[i])) smalls.splice(i, 1)
    smalls.forEach((o) => {
        sprites.push({ x: o.x, y: o.y, box: [-o.s * 1.7, -o.s * 2.4, o.s * 1.7, o.s * 0.8 + 4], kind: o.kind, sway: o.kind === "bush" ? 0.35 : 0, paint(ctx, pal) { painters[o.kind](ctx, pal, o.s, o.seed) } })
        if (o.kind === "rock" || o.kind === "log") footprints.push({ circle: true, x: o.x, y: o.y - 2, r: o.s * 0.7 })
    })
    // reeds around the pond, the lake and along the river banks
    const reeds = (x, y, seed) => sprites.push({ x, y, box: [-14, -34, 14, 4], kind: "reeds", sway: 0.8, paint(ctx, pal) { paintReeds(ctx, pal, 14, seed) } })
    for (let i = 0; i < 16; i++) {
        const a = r() * Math.PI * 2
        if (Math.sin(a) > 0.55 && Math.cos(a) > -0.2 && Math.cos(a) < 0.6) continue // leave the dock side open
        reeds(pond.x + Math.cos(a) * (pond.rx + 2), pond.y + Math.sin(a) * (pond.ry + 2), i + 7)
    }
    if (lake) {
        for (let i = 0; i < 26; i++) {
            const a = r() * Math.PI * 2
            if (Math.cos(a) > 0.55) continue // the harbour side stays open
            reeds(lake.x + Math.cos(a) * (lake.rx + 3), lake.y + Math.sin(a) * (lake.ry + 3), i + 70)
        }
    }
    mainlandRivers.forEach((rv) => {
        rv.samples.forEach((p, i) => {
            if (i % 9 || r() < 0.4 || onBridge(p.x, p.y)) return
            const side = r() < 0.5 ? -1 : 1
            const x = p.x + p.nx * (rv.width / 2 + 4) * side, y = p.y + p.ny * (rv.width / 2 + 4) * side
            if (nearPath(x, y, 26) || transport.near(x, y, 16)) return
            reeds(x, y, i + 200)
        })
    })

    // --- the Northern Reach: lamps along its roads, then its own nature
    reachPaths.forEach((p, pi) => {
        const samples = reachSamples[pi]
        for (let i = 30; i < samples.length - 24; i += 48) {
            const s = samples[i], side = (i / 48 + pi) % 2 ? 1 : -1
            const x = s.x + s.nx * 34 * side, y = s.y + s.ny * 34 * side
            if (!clearOf(x, y, -18, "reach") || inSea(x, y, 20)) continue
            if (lampHash.any(x, y, 210, (o) => Math.hypot(o.x - x, o.y - y) < 210)) continue
            if (treeHash.any(x, y, 40, (o) => Math.hypot(o.x - x, o.y - y) < (o.s || 20) * 0.5 + 10)) continue
            if (nearTrail(x, y, 20) || transport.near(x, y, 14)) continue
            const lamp = lampPost(x, y)
            sprites.push({ ...lamp, kind: "lamp" })
            lampHash.insert({ x, y }, x, y)
            lights.push(lamp.light)
            footprints.push({ circle: true, x, y: y - 2, r: 5 })
        }
    })
    const grown = reach.grow({
        clearOf: (x, y, pad) => clearOf(x, y, pad, "reach") && !lampHash.any(x, y, 30, (o) => Math.hypot(o.x - x, o.y - y) < 26),
        // the trails came later: clear the few things standing on them
        keep: (o) => !nearTrail(o.x, o.y, 30 + (o.s || 10) * 0.6) && offLine(o),
        spaced: (x, y, size) => !treeHash.any(x, y, size * 2.2 + 40, (o) => Math.hypot(o.x - x, o.y - y) < ((o.s || 20) + size) * 0.95),
        note: (o) => treeHash.insert(o, o.x, o.y),
        inSea
    })
    sprites.push(...grown.sprites)
    footprints.push(...grown.footprints)
    const peaksOpts = {
        clearOf: (x, y, pad) => clearOf(x, y, pad, "peaks"),
        // the south's trails came later: clear what stands on them
        keep: (o) => !nearSouthTrail(o.x, o.y - (o.kind === "mountain" ? o.s * 0.1 : 0), 30 + (o.s || 10) * (o.kind === "mountain" ? 0.95 : 0.6)) && (o.kind === "mountain" ? offLine({ x: o.x, y: o.y - o.s * 0.1, s: o.s }, 0.95) : offLine(o)),
        spaced: (x, y, size) => !treeHash.any(x, y, size * 2.2 + 40, (o) => Math.hypot(o.x - x, o.y - y) < ((o.s || 20) + size) * 0.95),
        note: (o) => treeHash.insert(o, o.x, o.y)
    }
    const grownPeaks = peaks.grow(peaksOpts)
    sprites.push(...grownPeaks.sprites)
    footprints.push(...grownPeaks.footprints)
    const growOpts = {
        clearOf: (x, y, pad) => clearOf(x, y, pad),
        spaced: (x, y, size) => !treeHash.any(x, y, size * 2.2 + 40, (o) => Math.hypot(o.x - x, o.y - y) < ((o.s || 20) + size) * 0.95),
        note: (o) => treeHash.insert(o, o.x, o.y),
        keep: (o) => (o.kind === "mountain" ? offLine({ x: o.x, y: o.y - o.s * 0.1, s: o.s }, 0.95) : offLine(o)),
        inSea
    }
    const grownIsles = isles.grow(growOpts)
    const grownDunes = dunes.grow(growOpts)
    const grownWild = wildwood.grow(growOpts)
    ;[grownIsles, grownDunes, grownWild].forEach((g) => { sprites.push(...g.sprites); footprints.push(...g.footprints) })
    // the Peaks' valley floor above the Wildwood fills in last, so nothing above moves
    const valley = peaks.growValley({ ...peaksOpts, clearOf: (x, y, pad) => clearOf(x, y, pad) })
    sprites.push(...valley.sprites); footprints.push(...valley.footprints)
    grownPeaks.trees.push(...valley.trees); grownPeaks.smalls.push(...valley.smalls)
    // small props that happen to stand on a line (a beach umbrella) give way to it
    for (let i = sprites.length - 1; i >= 0; i--) {
        const sp = sprites[i]
        if ((sp.kind === "built" || sp.kind === "lamp") && sp.box[2] - sp.box[0] < 70 && transport.near(sp.x, sp.y, 6)) sprites.splice(i, 1)
    }
    for (let i = footprints.length - 1; i >= 0; i--) {
        const f = footprints[i]
        if ((f.circle ? f.r * 2 : Math.max(f.w, f.h)) < 40 && transport.near(f.circle ? f.x : f.x + f.w / 2, f.circle ? f.y : f.y + f.h / 2, 4)) footprints.splice(i, 1)
    }
    sprites.push(...transport.sprites)
    footprints.push(...transport.footprints)
    lights.push(...transport.lights)

    // lily pads float on the pond and the lake; drawn per frame so they bob
    const lilies = []
    ;[[pond, 9], [lake, 14]].forEach(([e, count]) => {
        if (!e) return
        for (let i = 0; i < count; i++) {
            const a = r() * Math.PI * 2, d = 0.35 + r() * 0.5
            const x = e.x + Math.cos(a) * e.rx * d, y = e.y + Math.sin(a) * e.ry * d
            if (e === lake && x > lake.x + lake.rx * 0.35) continue // not in the harbour's way
            lilies.push({ x, y, s: 7 + r() * 5, rot: r() * Math.PI * 2, flower: r() < 0.4 })
        }
    })

    const footHash = new SpatialHash(64)
    footprints.forEach((f) => {
        if (f.circle) footHash.insert(f, f.x, f.y, f.r)
        else footHash.insert(f, f.x + f.w / 2, f.y + f.h / 2, Math.hypot(f.w, f.h) / 2)
    })
    const hitsFootprint = (x, y) => footHash.any(x, y, 1, (f) => (f.circle ? Math.hypot(f.x - x, f.y - y) < f.r : x > f.x && x < f.x + f.w && y > f.y && y < f.y + f.h))
    const onWalkway = (x, y) => walkways.some((w) => x > w.x - 2 && x < w.x + w.w + 2 && y > w.y - 2 && y < w.y + w.h + 2)

    function blocked(x, y) {
        if (x < 12 || y < 12 || x > W - 12 || y > H - 12) return true
        if (inPond(x, y, 4) && !onDock(x, y)) return true
        if (inLake(x, y, 4) && !onWalkway(x, y)) return true
        if (inRiver(x, y, 2) && !onBridge(x, y)) return true
        if (peaks.inLake(x, y, 4)) return true
        if (inLandWater(x, y, 4)) return true
        if (inSeaPolygon(x, y) && !onWalkway(x, y) && !islandAt(x, y, -8) && !reach.onIce(x, y) && !reach.onFerry(x, y)) return true
        return hitsFootprint(x, y)
    }
    // a little jetty out over the pond from its lower shore
    const dock = { x: pond.x + 36, y: pond.y + pond.ry * 0.2, w: 30, h: pond.ry * 0.95 }
    function onDock(x, y) { return x > dock.x - 2 && x < dock.x + dock.w + 2 && y > dock.y && y < dock.y + dock.h + 20 }

    // --- walking a long way: A* over a coarse grid of walkable cells
    // The grid is built a few rows at a time in idle moments (prepareGrid),
    // and finished on the spot if someone clicks before it's done.
    const CELL = 20
    const cols = Math.ceil(W / CELL), rows = Math.ceil(H / CELL)
    const grid = new Uint8Array(cols * rows)
    let gridRow = 0
    function buildRows(budgetMs) {
        const until = performance.now() + budgetMs
        while (gridRow < rows && performance.now() < until) {
            const cy = gridRow++
            for (let cx = 0; cx < cols; cx++) {
                const x = cx * CELL + CELL / 2, y = cy * CELL + CELL / 2
                grid[cy * cols + cx] = blocked(x, y) || blocked(x - 7, y) || blocked(x + 7, y) || blocked(x, y - 7) || blocked(x, y + 7) ? 0 : 1
            }
        }
        return gridRow >= rows
    }
    function prepareGrid(budgetMs = 6) { return buildRows(budgetMs) }
    function walkGrid() {
        if (gridRow < rows) buildRows(Infinity)
        return grid
    }
    function findPath(from, to) {
        const g = walkGrid()
        const cell = (p) => [Math.max(0, Math.min(cols - 1, Math.floor(p.x / CELL))), Math.max(0, Math.min(rows - 1, Math.floor(p.y / CELL)))]
        let [sx, sy] = cell(from)
        let [tx, ty] = cell(to)
        // aim for the nearest open cell to wherever was clicked
        if (!g[ty * cols + tx]) {
            let best = null
            for (let rad = 1; rad < 12 && !best; rad++) {
                for (let dy = -rad; dy <= rad; dy++) for (let dx = -rad; dx <= rad; dx++) {
                    const x = tx + dx, y = ty + dy
                    if (x >= 0 && y >= 0 && x < cols && y < rows && g[y * cols + x]) { if (!best || Math.hypot(dx, dy) < best.d) best = { x, y, d: Math.hypot(dx, dy) } }
                }
            }
            if (!best) return null
            tx = best.x; ty = best.y
        }
        g[sy * cols + sx] = 1
        const start = sy * cols + sx, goal = ty * cols + tx
        const gScore = new Float32Array(cols * rows).fill(Infinity)
        const came = new Int32Array(cols * rows).fill(-1)
        const heap = new MinHeap()
        gScore[start] = 0
        heap.push(start, 0)
        // a slightly greedy heuristic: across a map this size, routes round the
        // sea would otherwise explore most of the grid before settling
        const h = (i) => { const x = i % cols, y = (i / cols) | 0; const dx = Math.abs(x - tx), dy = Math.abs(y - ty); return (Math.max(dx, dy) + 0.414 * Math.min(dx, dy)) * 1.2 }
        const closed = new Uint8Array(cols * rows)
        let steps = 0
        while (heap.size && steps++ < 1500000) {
            const cur = heap.pop()
            if (cur === goal) break
            if (closed[cur]) continue // a stale duplicate
            closed[cur] = 1
            const cx = cur % cols, cy = (cur / cols) | 0
            for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
                if (!dx && !dy) continue
                const nx = cx + dx, ny = cy + dy
                if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue
                const ni = ny * cols + nx
                if (!g[ni]) continue
                if (dx && dy && (!g[cy * cols + nx] || !g[ny * cols + cx])) continue // no corner cutting
                const cost = gScore[cur] + (dx && dy ? 1.414 : 1)
                if (cost < gScore[ni]) { gScore[ni] = cost; came[ni] = cur; heap.push(ni, cost + h(ni)) }
            }
        }
        if (came[goal] === -1 && goal !== start) return null
        const cells = []
        for (let i = goal; i !== -1 && i !== start; i = came[i]) cells.push(i)
        cells.reverse()
        // string-pull: keep only the corners we can't see past
        const pts = cells.map((i) => ({ x: (i % cols) * CELL + CELL / 2, y: ((i / cols) | 0) * CELL + CELL / 2 }))
        const clear = (a, b) => {
            const d = Math.hypot(b.x - a.x, b.y - a.y), steps2 = Math.ceil(d / (CELL / 2))
            for (let k = 1; k < steps2; k++) {
                const x = a.x + ((b.x - a.x) * k) / steps2, y = a.y + ((b.y - a.y) * k) / steps2
                if (!g[Math.floor(y / CELL) * cols + Math.floor(x / CELL)]) return false
            }
            return true
        }
        const out = []
        let anchor = { x: from.x, y: from.y }
        for (let i = 0; i < pts.length; i++) {
            const next = pts[i + 1]
            if (!next || !clear(anchor, next)) { out.push(pts[i]); anchor = pts[i] }
        }
        if (out.length && g[Math.floor(to.y / CELL) * cols + Math.floor(to.x / CELL)] && !blocked(to.x, to.y)) out[out.length - 1] = { x: to.x, y: to.y }
        return out
    }

    // Paints the ground, all of it or just `region` ({ x, y, w, h }): the
    // map's sharp close-up tiles are painted one region at a time. Every
    // random sequence here is fresh from the seed and consumed the same way
    // whatever the region, so a tile matches the whole-map painting exactly
    // (and a theme switch doesn't reshuffle the meadow).
    function paintGround(ctx, pal, region = null) {
        const vis = region
            ? (x, y, rad = 0) => x + rad > region.x && x - rad < region.x + region.w && y + rad > region.y && y - rad < region.y + region.h
            : () => true
        paintMeadow(ctx, pal, W, H, n, rng(config.seed + 3), vis, Wm, Hm)
        reach.paintLand(ctx, pal, vis)
        reach.paintGlacier(ctx, pal, vis)
        peaks.paintLand(ctx, pal, vis)
        dunes.paintLand(ctx, pal, vis)
        wildwood.paintLand(ctx, pal, vis)
        districts.fields.forEach((paint) => paint(ctx, pal)) // farmland lies under everything
        // soft shadows under everything upright, cast to the lower right
        trees.forEach((t) => { if (vis(t.x, t.y, t.s * 2)) softShadow(ctx, t.x + t.s * 0.45, t.y + 2, t.s * 1.25, t.s * 0.5, pal.shadowAlpha, pal.shadow) })
        smalls.forEach((o) => { if (vis(o.x, o.y, o.s * 2)) softShadow(ctx, o.x + o.s * 0.3, o.y + 1, o.s * 1.2, o.s * 0.45, pal.shadowAlpha * 0.8, pal.shadow) })
        ;[...built.sprites, ...districts.sprites].forEach((s) => { if (vis(s.x, s.y, 200)) softShadow(ctx, s.x + 10, s.y + 2, Math.max(16, (s.box[2] - s.box[0]) * 0.55), 12, pal.shadowAlpha, pal.shadow) })
        reach.paintShadows(ctx, pal, vis, grown)
        peaks.paintShadows(ctx, pal, vis, grownPeaks)
        isles.paintShadows(ctx, pal, vis, grownIsles)
        dunes.paintShadows(ctx, pal, vis, grownDunes)
        wildwood.paintShadows(ctx, pal, vis, grownWild)
        reach.paintShore(ctx, pal, vis)
        peaks.paintShore(ctx, pal, vis)
        isles.paintShore(ctx, pal, vis)
        dunes.paintShore(ctx, pal, vis)
        if (coast) paintSea(ctx, pal, seaPolygon, coast, n, vis, config.seed, [[...northCoast, ...peaksCoast.slice(1), ...(southShores[0] || []).slice(1), ...(southShores[1] || []).slice(1)], southShores[2] || []])
        reach.paintSeaTint(ctx, pal, seaPolygon, vis)
        reach.paintShoreTexture(ctx, pal, vis)
        reach.paintIslands(ctx, pal, vis)
        reach.paintIce(ctx, pal, vis)
        peaks.paintLakes(ctx, pal, vis)
        isles.paintAfterSea(ctx, pal, seaPolygon, vis)
        dunes.paintAfterSea(ctx, pal, vis)
        rivers.forEach((rv) => paintRiver(ctx, pal, rv, n, vis))
        wildwood.paintOverRiver(ctx, pal, vis)
        if (lake && vis(lake.x, lake.y, lake.rx + 30)) paintPond(ctx, pal, lake, null, n)
        // clearings under the landmarks, then the roads over them
        landmarks.forEach((l, i) => { if (!l.line && vis(l.x, l.y, l.r + 20) && !reach.onIce(l.x, l.y)) clearing(ctx, groundPal(pal, l.x, l.y), l.x, l.y + 10, l.r * 0.82, l.r * 0.5, 500 + i) })
        if (vis(spawn.x, spawn.y, 120)) clearing(ctx, pal, spawn.x, spawn.y + 12, 84, 40, 590)
        Object.values(junctions).forEach((j, i) => { if (vis(j.x, j.y, 60)) clearing(ctx, groundPal(pal, j.x, j.y), j.x, j.y, 34, 22, 700 + i) })
        paintPaths(ctx, pal, pathSamples, n, vis)
        if (reachSamples.length) {
            // dirt through the heath, a trodden trail once it reaches the snow
            paintPaths(ctx, pal, reachSamples, n, vis)
            ctx.save()
            ctx.beginPath(); reach.snowPoly.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip()
            paintPaths(ctx, reach.trailPal(pal), reachSamples, n, vis)
            ctx.restore()
        }
        trailSamples.forEach((samples) => paintTrail(ctx, pal, samples, vis, (x, y) => groundPal(pal, x, y) !== pal))
        bridges.forEach((b) => { if (vis((b.x1 + b.x2) / 2, (b.y1 + b.y2) / 2, 80)) paintBridge(ctx, pal, b) })
        built.decals.forEach((paint) => paint(ctx, pal))
        districts.decals.forEach((paint) => paint(ctx, pal))
        reach.paintFerry(ctx, pal, vis)
        if (vis(pond.x, pond.y, pond.rx + 40)) paintPond(ctx, pal, pond, dock, n)
        // wildflowers and tufts where nothing else is
        const fr = rng(config.seed + 5)
        for (let i = 0; i < 90 * area; i++) {
            const x = fr() * Wm, y = fr() * Hm
            if (!vis(x, y, 20) || !clearOf(x, y, -10)) continue
            paintFlowerClump(ctx, pal, x, y, i + 300)
        }
        ctx.lineWidth = 1.1
        for (let i = 0; i < 2400 * area; i++) {
            const x = fr() * Wm, y = fr() * Hm, h = 4 + fr() * 4
            if (!vis(x, y, 8)) continue
            if (inPond(x, y, 2) || inLake(x, y, 2) || inRiver(x, y, 2) || inSea(x, y, 90) || nearPath(x, y, 16)) continue
            const v = n(x / 90, y / 90)
            ctx.globalAlpha = 0.35 + v * 0.4
            paintTuft(ctx, v > 0.55 ? pal.tuftLight : pal.tuft, x, y, h)
        }
        ctx.globalAlpha = 1
        reach.paintDetails(ctx, pal, vis, clearOf, inSea)
        peaks.paintDetails(ctx, pal, vis, clearOf, inSea)
        isles.paintDetails(ctx, pal, vis, clearOf)
        dunes.paintDetails(ctx, pal, vis, clearOf, inSea)
        wildwood.paintDetails(ctx, pal, vis, clearOf, inSea)
        // the railway last, over whatever it crosses
        transport.paintGround(ctx, pal, vis)
        transport.sprites.forEach((sp) => { if (vis(sp.x, sp.y, 160) && sp.box[2] - sp.box[0] > 60) softShadow(ctx, sp.x + 10, sp.y + 2, (sp.box[2] - sp.box[0]) * 0.5, 12, pal.shadowAlpha, pal.shadow) })
    }

    // open ground for butterflies and fireflies to wander over
    const meadowSpots = []
    for (let i = 0; i < 1600 && meadowSpots.length < 60 * Math.min(area, 3); i++) {
        const x = 60 + r() * (Wm - 120), y = 60 + r() * (Hm - 120)
        if (clearOf(x, y, 0, false)) meadowSpots.push({ x, y })
    }

    return {
        W, H, landmarks, byKey, pond, lake, dock, lilies, sprites, lights, blocked, findPath, prepareGrid, paintGround,
        anchors: { ...built.anchors, ...districts.anchors }, meadowSpots, trees: [...trees, ...grown.trees, ...grownPeaks.trees, ...grownIsles.trees, ...grownDunes.trees, ...grownWild.trees], river, rivers, bridges, junctions, coast, inSea,
        northCoast, peaksCoast, southShores, reach, peaks, isles, dunes, wildwood, transport, mainlandWidth: Wm, biome, islandAt, onFerry: (x, y) => reach.onFerry(x, y, 26) && inSeaPolygon(x, y) && !islandAt(x, y, -8)
    }
}

// A binary min-heap of (index, priority), for the pathfinder.
class MinHeap {
    constructor() { this.items = []; this.prios = [] }
    get size() { return this.items.length }
    push(item, prio) {
        const a = this.items, p = this.prios
        a.push(item); p.push(prio)
        let i = a.length - 1
        while (i > 0) {
            const parent = (i - 1) >> 1
            if (p[parent] <= p[i]) break
            ;[a[i], a[parent]] = [a[parent], a[i]]; [p[i], p[parent]] = [p[parent], p[i]]
            i = parent
        }
    }
    pop() {
        const a = this.items, p = this.prios
        const top = a[0]
        const lastItem = a.pop(), lastPrio = p.pop()
        if (a.length) {
            a[0] = lastItem; p[0] = lastPrio
            let i = 0
            for (;;) {
                const l = 2 * i + 1, rr = l + 1
                let m = i
                if (l < a.length && p[l] < p[m]) m = l
                if (rr < a.length && p[rr] < p[m]) m = rr
                if (m === i) break
                ;[a[i], a[m]] = [a[m], a[i]]; [p[i], p[m]] = [p[m], p[i]]
                i = m
            }
        }
        return top
    }
}

// The river: a muddy bank, water deepening toward the middle, and pale
// current lines (the motion layer adds flowing glints on top).
function paintRiver(ctx, pal, river, n, vis = () => true) {
    const w = river.width
    const near = river.samples.filter((p) => vis(p.x, p.y, w))
    const stamp = (extra, color) => {
        ctx.fillStyle = color
        near.forEach((p) => { ctx.beginPath(); ctx.arc(p.x, p.y, w / 2 + extra + (n(p.x / 70, p.y / 70) - 0.5) * 6, 0, Math.PI * 2); ctx.fill() })
    }
    stamp(10, pal.shore)
    stamp(2.2, pal.ink)
    stamp(0, pal.water)
    ctx.fillStyle = pal.waterDeep
    near.forEach((p) => { ctx.beginPath(); ctx.arc(p.x, p.y, w * 0.26, 0, Math.PI * 2); ctx.fill() })
    ctx.strokeStyle = withAlpha(pal.foam, 0.55)
    ctx.lineWidth = 1.3
    river.samples.forEach((p, i) => {
        if (i % 5 || !vis(p.x, p.y, w)) return
        const o = (n(i, 3) - 0.5) * w * 0.6
        ctx.beginPath()
        ctx.moveTo(p.x + p.nx * o - p.ny * 6, p.y + p.ny * o + p.nx * 6)
        ctx.lineTo(p.x + p.nx * o + p.ny * 6, p.y + p.ny * o - p.nx * 6)
        ctx.stroke()
    })
}

// The sea: a sandy beach along the coastline, then water that deepens away
// from the shore in bands, and a line of foam where it meets the sand (the
// motion layer rolls waves in along it).
// `more` is the Reach's shingle coast: the water deepens off it too, but its
// beach is shingle, painted by north.js.
function paintSea(ctx, pal, polygon, coast, n, vis, seed, more = []) {
    const near = [...coast, ...more.flat()].filter((p) => vis(p.x, p.y, 320))
    if (!near.length && !polygon.some(([x, y]) => vis(x, y, 400))) {
        // the region may lie wholly out at sea: fill it if so
        ctx.save(); ctx.beginPath(); polygon.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath()
        ctx.fillStyle = pal.seaDeep; ctx.fill(); ctx.restore()
        return
    }
    const along = (width, color, lines = [coast]) => {
        ctx.strokeStyle = color; ctx.lineWidth = width; ctx.lineCap = "round"; ctx.lineJoin = "round"
        lines.forEach((pts) => { if (pts.length) { ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.stroke() } })
    }
    const all = [coast, ...more]
    // dry sand, then wet sand nearer the water
    along(250, pal.sand)
    along(90, pal.sandDark)
    // the water, deepening offshore
    ctx.save()
    ctx.beginPath(); polygon.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath()
    ctx.fillStyle = pal.seaDeep; ctx.fill()
    ctx.clip()
    along(520, pal.sea, all)
    along(200, pal.seaShallow, all)
    along(70, withAlpha(pal.foam, 0.45), all)
    ctx.restore()
    // the foam line on the shore
    ctx.save()
    ctx.globalAlpha = 0.85
    along(3, pal.foam, all)
    ctx.restore()
    // sand texture, shells and pebbles above the tideline
    const r = rng(seed + 61)
    coast.forEach((p) => {
        for (let k = 0; k < 6; k++) {
            const o = 10 + r() * 110, jitter = (r() - 0.5) * 16, size = 0.8 + r() * 1.4, kind = r()
            if (!vis(p.x, p.y, 130)) continue
            const x = p.x - p.nx * o + p.ny * jitter, y = p.y - p.ny * o - p.nx * jitter
            ctx.fillStyle = kind < 0.08 ? "#F6E7DA" : kind < 0.14 ? pal.pebble : withAlpha(pal.sandDark, 0.6)
            ctx.beginPath(); ctx.arc(x, y, kind < 0.14 ? size + 1.2 : size, 0, Math.PI * 2); ctx.fill()
        }
    })
}

// A wooden footbridge across the river, planks running across the deck.
function paintBridge(ctx, pal, b) {
    const len = Math.hypot(b.x2 - b.x1, b.y2 - b.y1), half = 17
    ctx.save()
    ctx.translate(b.x1, b.y1)
    ctx.rotate(b.angle)
    softShadow(ctx, len / 2 + 6, half + 6, len / 2 + 6, 10, 0.3, pal.shadow)
    ctx.fillStyle = pal.woodLight
    ctx.fillRect(0, -half, len, half * 2)
    ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.6; ctx.strokeRect(0, -half, len, half * 2)
    for (let x = 7; x < len; x += 8) line(ctx, [[x, -half], [x, half]], pal.woodDark, 1, 0.7)
    // rails
    ;[-half - 2, half + 2].forEach((y) => {
        line(ctx, [[0, y], [len, y]], pal.ink, 4.4)
        line(ctx, [[0, y], [len, y]], pal.wood, 2.6)
        for (let x = 0; x <= len; x += len / 4) inkEllipse(ctx, x, y, 3.2, 3.2, pal.woodDark, pal.ink, 1.1)
    })
    ctx.restore()
}

// ---- ground painters ------------------------------------------------------------

// `Wm` is the original map's width: the meadow's scatter is grown across
// that, so it never reshuffles; the fill and the darkened edges span the whole
// world (the Reach paints over its half).
function paintMeadow(ctx, pal, W, H, n, r, vis = () => true, Wm = W, Hm = H) {
    const area = (Wm * Hm) / (2000 * 1250)
    ctx.fillStyle = pal.ground
    ctx.fillRect(0, 0, W, H)
    // big soft washes of lighter, darker and sunnier green
    for (let i = 0; i < 70 * area; i++) {
        const x = r() * Wm, y = r() * Hm, rad = 80 + r() * 220, a = 0.35 + r() * 0.3
        if (!vis(x, y, rad)) continue
        const v = n(x / 300, y / 300)
        const color = v > 0.62 ? pal.groundWarm : v > 0.45 ? pal.groundLight : pal.groundDark
        wash(ctx, x, y, rad, color, a)
    }
    // mottled texture: tiny dabs following the noise
    for (let i = 0; i < 9000 * area; i++) {
        const x = r() * Wm, y = r() * Hm
        if (!vis(x, y, 4)) continue
        const v = n(x / 40, y / 40)
        ctx.fillStyle = v > 0.6 ? withAlpha(pal.groundLight, 0.5) : v < 0.35 ? withAlpha(pal.groundDark, 0.45) : withAlpha(pal.ground, 0.3)
        ctx.beginPath(); ctx.ellipse(x, y, 2.4, 1.3, 0, 0, Math.PI * 2); ctx.fill()
    }
    // the woods darken toward the edges
    const edge = 150
    const grads = [
        [0, 0, edge, 0, 0, 0, edge, H], [W, 0, W - edge, 0, W - edge, 0, edge, H],
        [0, 0, 0, edge, 0, 0, W, edge], [0, H, 0, H - edge, 0, H - edge, W, edge]
    ]
    grads.forEach(([x0, y0, x1, y1, rx, ry, rw, rh]) => {
        const g = ctx.createLinearGradient(x0, y0, x1, y1)
        g.addColorStop(0, withAlpha(pal.groundEdge, 0.75)); g.addColorStop(1, withAlpha(pal.groundEdge, 0))
        ctx.fillStyle = g; ctx.fillRect(rx, ry, rw, rh)
    })
}

function clearing(ctx, pal, x, y, rx, ry, seed) {
    ctx.save()
    blobPath(ctx, x, y, rx + 4, ry + 3, seed, 0.1)
    ctx.fillStyle = withAlpha(pal.pathDark, 0.55); ctx.fill()
    blobPath(ctx, x, y, rx, ry, seed, 0.1)
    const g = ctx.createRadialGradient(x - rx * 0.2, y - ry * 0.3, 4, x, y, rx)
    g.addColorStop(0, pal.pathLight); g.addColorStop(1, pal.path)
    ctx.fillStyle = g; ctx.fill()
    ctx.restore()
    speckle(ctx, pal, x, y, rx, ry, seed)
}

function speckle(ctx, pal, x, y, rx, ry, seed) {
    const r = rng(seed + 11)
    for (let i = 0; i < rx * ry * 0.02; i++) {
        const a = r() * Math.PI * 2, d = Math.sqrt(r())
        ctx.fillStyle = r() < 0.5 ? withAlpha(pal.pathDark, 0.5) : withAlpha(pal.pathLight, 0.8)
        ctx.beginPath(); ctx.arc(x + Math.cos(a) * rx * d * 0.95, y + Math.sin(a) * ry * d * 0.95, 0.8 + r() * 1.2, 0, Math.PI * 2); ctx.fill()
    }
}

// Worn dirt paths: an organic edge stamped along each curve, a lighter worn
// middle, speckles, and pebbles along the verges.
function paintPaths(ctx, pal, pathSamples, n, vis = () => true) {
    const width = (p, k) => 17 + (n(p.x / 60, p.y / 60) - 0.5) * 8 + k
    const near = pathSamples.map((s) => s.filter((p) => vis(p.x, p.y, 30)))
    ctx.fillStyle = withAlpha(pal.pathDark, 0.9)
    near.forEach((s) => s.forEach((p) => { ctx.beginPath(); ctx.arc(p.x, p.y, width(p, 3.5), 0, Math.PI * 2); ctx.fill() }))
    ctx.fillStyle = pal.path
    near.forEach((s) => s.forEach((p) => { ctx.beginPath(); ctx.arc(p.x, p.y, width(p, 0), 0, Math.PI * 2); ctx.fill() }))
    // the worn middle: one soft stroke down each road (stamped circles showed
    // as beads up close)
    ctx.save()
    ctx.strokeStyle = withAlpha(pal.pathLight, 0.6)
    ctx.lineWidth = 15
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    pathSamples.forEach((s, si) => {
        if (s.length < 2 || !near[si].length) return // whole road, so a curve leaving the region never shortcuts across it
        ctx.beginPath()
        s.forEach((p, i) => (i ? ctx.lineTo(p.x + p.nx * 3, p.y + p.ny * 3) : ctx.moveTo(p.x + p.nx * 3, p.y + p.ny * 3)))
        ctx.stroke()
    })
    ctx.restore()
    // speckles and pebbles: the random draws happen for every sample, drawn
    // or not, so the sequence (and the look) doesn't depend on the region
    const r = rng(77)
    pathSamples.forEach((s) => s.forEach((p, i) => {
        const show = vis(p.x, p.y, 30)
        if (r() < 0.5) {
            const dark = r() < 0.5, o = (r() - 0.5) * 26, size = 0.8 + r()
            if (show) {
                ctx.fillStyle = dark ? withAlpha(pal.pathDark, 0.55) : withAlpha(pal.pathLight, 0.9)
                ctx.beginPath(); ctx.arc(p.x + p.nx * o, p.y + p.ny * o, size, 0, Math.PI * 2); ctx.fill()
            }
        }
        if (i % 7 === 0 && r() < 0.6) {
            const side = r() < 0.5 ? -1 : 1, w = width(p, 1), rx = 2.5 + r() * 2.5, ry = 1.8 + r() * 1.2
            if (show) inkEllipse(ctx, p.x + p.nx * w * side, p.y + p.ny * w * side, rx, ry, pal.pebble, withAlpha(pal.ink, 0.5), 0.8)
        }
    }))
}

// A trail: narrower than a road, worn dirt through the meadows and a trodden
// line of pale snow where `snowy(x, y)` says it crosses snow.
function paintTrail(ctx, pal, samples, vis, snowy) {
    const near = samples.filter((p) => vis(p.x, p.y, 20))
    if (!near.length) return
    ctx.fillStyle = withAlpha(pal.pathDark, 0.75)
    near.forEach((p) => { ctx.beginPath(); ctx.arc(p.x, p.y, 11, 0, Math.PI * 2); ctx.fill() })
    near.forEach((p) => { ctx.fillStyle = snowy(p.x, p.y) ? pal.snowTrail : pal.path; ctx.beginPath(); ctx.arc(p.x, p.y, 8.5, 0, Math.PI * 2); ctx.fill() })
    const r = rng(79)
    samples.forEach((p, i) => {
        const show = vis(p.x, p.y, 20), o = (r() - 0.5) * 14, kind = r()
        if (!show || i % 3) return
        ctx.fillStyle = kind < 0.5 ? withAlpha(pal.pathDark, 0.5) : withAlpha(pal.pathLight, 0.8)
        ctx.beginPath(); ctx.arc(p.x + p.nx * o, p.y + p.ny * o, 0.9 + kind, 0, Math.PI * 2); ctx.fill()
    })
}

function paintPond(ctx, pal, pond, dock, n) {
    const { x, y, rx, ry } = pond
    // muddy shore, then water deepening toward the middle
    blobPath(ctx, x, y + 3, rx + 12, ry + 9, 31, 0.05, 30)
    ctx.fillStyle = pal.shore; ctx.fill()
    blobPath(ctx, x, y, rx, ry, 32, 0.04, 30)
    const g = ctx.createRadialGradient(x - rx * 0.15, y - ry * 0.1, 10, x, y, rx)
    g.addColorStop(0, pal.waterDeep); g.addColorStop(0.7, pal.water); g.addColorStop(1, pal.waterLight)
    ctx.fillStyle = g; ctx.fill()
    ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.8; ctx.stroke()
    // shallows band and ripples
    ctx.save()
    blobPath(ctx, x, y, rx, ry, 32, 0.04, 30); ctx.clip()
    ctx.strokeStyle = withAlpha(pal.foam, 0.5); ctx.lineWidth = 5
    blobPath(ctx, x, y, rx - 4, ry - 4, 33, 0.04, 30); ctx.stroke()
    ctx.strokeStyle = withAlpha(pal.foam, 0.6); ctx.lineWidth = 1.3
    for (let i = 0; i < 14; i++) {
        const px = x + (n(i, 1) - 0.5) * rx * 1.4, py = y + (n(1, i) - 0.5) * ry * 1.3
        ctx.beginPath(); ctx.ellipse(px, py, 9 + i % 4 * 3, 2.2, 0, Math.PI * 0.1, Math.PI * 0.9); ctx.stroke()
    }
    // a sky reflection
    ctx.fillStyle = withAlpha("#FFFFFF", pal.night ? 0.06 : 0.16)
    ctx.beginPath(); ctx.ellipse(x - rx * 0.35, y - ry * 0.35, rx * 0.35, ry * 0.14, -0.2, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
    if (!dock) return
    // the jetty
    softShadow(ctx, dock.x + dock.w / 2 + 6, dock.y + dock.h / 2, dock.w, dock.h / 2, 0.25, pal.shadow)
    ctx.fillStyle = pal.woodLight
    ctx.fillRect(dock.x, dock.y, dock.w, dock.h + 16)
    ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.4; ctx.strokeRect(dock.x, dock.y, dock.w, dock.h + 16)
    for (let py = dock.y + 8; py < dock.y + dock.h + 16; py += 8) line(ctx, [[dock.x, py], [dock.x + dock.w, py]], pal.woodDark, 1, 0.7)
    ;[[dock.x - 2, dock.y + 2], [dock.x + dock.w - 2, dock.y + 2]].forEach(([px, py]) => inkEllipse(ctx, px + 2, py, 3.5, 3.5, pal.woodDark, pal.ink, 1.1))
}

// Lily pads, drawn each frame over the ground (they bob in the motion pass).
export function paintLily(ctx, pal, pad, bob = 0) {
    ctx.save()
    ctx.translate(pad.x, pad.y + bob)
    ctx.rotate(pad.rot)
    ctx.beginPath()
    ctx.moveTo(0, 0); ctx.arc(0, 0, pad.s, 0.35, Math.PI * 2 - 0.1); ctx.closePath()
    ctx.fillStyle = pal.leaf; ctx.fill()
    ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.1; ctx.stroke()
    ctx.strokeStyle = pal.leafDark; ctx.lineWidth = 0.8
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(-pad.s * 0.7, 0); ctx.moveTo(0, 0); ctx.lineTo(pad.s * 0.4, -pad.s * 0.5); ctx.stroke()
    if (pad.flower) {
        ctx.fillStyle = pal.night ? "#C9B7C4" : "#F6C8D8"
        for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; ctx.beginPath(); ctx.ellipse(Math.cos(a) * 3, Math.sin(a) * 3, 3.2, 1.8, a, 0, Math.PI * 2); ctx.fill() }
        ctx.fillStyle = "#F2C14E"; ctx.beginPath(); ctx.arc(0, 0, 1.8, 0, Math.PI * 2); ctx.fill()
    }
    ctx.restore()
}

// A sprite's own canvas: painted once per palette at resolution k, then
// drawn each frame with drawImage (and optionally skewed to sway).
export function renderSprite(sprite, pal, k) {
    const [l, t, rgt, b] = sprite.box
    const w = Math.max(1, Math.ceil((rgt - l) * k)), h = Math.max(1, Math.ceil((b - t) * k))
    const canvas = document.createElement("canvas")
    canvas.width = w; canvas.height = h
    const ctx = canvas.getContext("2d")
    ctx.setTransform(k, 0, 0, k, -l * k, -t * k)
    sprite.paint(ctx, pal)
    return canvas
}

// A landmark's name on a little ribbon banner with forked ends, hung just
// below the place (the strip buildWorld keeps clear of trees).
export function paintLabel(ctx, pal, l, alpha = 1) {
    const text = l.label
    ctx.save()
    ctx.globalAlpha = alpha
    ctx.font = `700 19px ${HAND_FONT}`
    const w = ctx.measureText(text).width + 28, h = 26
    const x = l.x - w / 2, y = l.y + l.r * 0.56 + 8
    const tail = 12
    // the forked tails behind
    ;[-1, 1].forEach((side) => {
        const ex = side < 0 ? x + 6 : x + w - 6
        const ox = ex + side * (tail + 8)
        ctx.beginPath()
        ctx.moveTo(ex, y + 6); ctx.lineTo(ox, y + 6); ctx.lineTo(ox - side * 6, y + h / 2 + 3); ctx.lineTo(ox, y + h); ctx.lineTo(ex, y + h)
        ctx.closePath()
        ctx.fillStyle = pal.night ? "#3A4A48" : "#EAD9B4"; ctx.fill()
        ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.4; ctx.stroke()
    })
    softShadow(ctx, l.x + 4, y + h + 2, w * 0.55, 6, 0.18, pal.shadow)
    ctx.beginPath()
    ctx.moveTo(x, y); ctx.quadraticCurveTo(l.x, y - 5, x + w, y); ctx.lineTo(x + w, y + h - 6); ctx.quadraticCurveTo(l.x, y + h - 11, x, y + h - 6); ctx.closePath()
    ctx.fillStyle = pal.tagBg; ctx.fill()
    ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.6; ctx.stroke()
    ctx.fillStyle = pal.tagInk
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(text, l.x, y + h / 2 - 5)
    ctx.restore()
}
